import { useState, useRef, useEffect, useContext } from 'react';
import { Card, Col, Row, Nav } from 'react-bootstrap';
import useScrollSpy from 'react-use-scrollspy';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { parseISO, format } from 'date-fns';
import {
  DraftPrompt,
  DraftSavedNote,
  FormProvider
} from 'components/common/form';
import { ProfileInformation } from './ProfileInformation';
import { LocationCard } from './LocationCard';
import { ParentsDetails } from './ParentsDetails';
import { GuardianDetails } from './GuardianDetails';
import { NextOfKinDetails } from './NextOfKinDetails';
import { InspirationCard } from './InspirationCard';
import { SponsorshipStatus } from './SponsorshipStatus';
import AuthContext from 'context/authContext';
import LoadingButton from 'components/common/LoadingButton';
import { calculateAge } from 'helpers/utils';
import { beneficiaryScheme } from './beneficiaryScheme';
import useUpload from 'hooks/useUpload';
import { toast } from 'react-toastify';
import { SponsorshipTypeCard } from './SponsorshipTypeCard';
import { HealthConditionCard } from './HealthConditionCard';
import { beneficiaryDraftStore } from './beneficiaryDraft';
import useFormDraft from 'hooks/useFormDraft';
import {
  useSponsorshipStatuses,
  useSponsorshipConditions,
  useSponsorshipPriorities
} from 'hooks/useBeneficiaries';

export const BENEFICIARY_STATUS_SPONSORED = 'sponsored';
export const BENEFICIARY_STATUS_UNSPONSORED = 'unsponsored';

const BeneficiaryForm = ({ onSubmit, isEdit, data, costData, loading }) => {
  const { organization, selectedBranchId, user } = useContext(AuthContext);
  const { sponsorshipStatuses } = useSponsorshipStatuses();
  const { sponsorshipConditions } = useSponsorshipConditions();
  const { sponsorshipPriorities } = useSponsorshipPriorities();

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -150
  });

  const [stickyStatus, setStickyStatus] = useState(false);

  useEffect(() => {
    window.addEventListener('scroll', () => {
      if (window.scrollY < 100) {
        setStickyStatus(false);
      } else {
        setStickyStatus(true);
      }
    });
  }, []);

  const sideNavLinks = [
    { id: 1, href: '#info', title: 'Profile Information' },
    { id: 2, href: '#sponsorshipType', title: 'Sponsorship Types' },
    { id: 3, href: '#location', title: 'Location' },
    { id: 4, href: '#condition', title: 'Health Condition' },
    { id: 5, href: '#parents', title: 'Parents' },
    { id: 6, href: '#details', title: 'Current Guardian' },
    { id: 7, href: '#nextOfKin', title: 'Next of Kin' },
    { id: 8, href: '#inspiration', title: 'Inspiration' },
    { id: 9, href: '#sponsorshipStatus', title: 'Sponsorship Status' }
  ];

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      age: '',
      language: '',
      secondaryLanguage: '',
      hyConnectId: '',
      address: '',
      sponsorshipTypes: [],
      image: '',
      beneficiaryCategory: '',
      beneficiaryCode: '',
      enrollmentDate: format(new Date(), 'yyyy-MM-dd'),
      createdAt: format(new Date(), 'yyyy-MM-dd'),
      guardian: {
        inheritFrom: '',
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        relationship: null,
        occupation: '',
        monthlyIncomeRange: '',
        notes: ''
      },
      parents: {
        father: {
          first_name: '',
          middle_name: '',
          last_name: '',
          status: '',
          phone: '',
          email: '',
          address: '',
          occupation: '',
          monthly_income_range: ''
        },
        mother: {
          first_name: '',
          middle_name: '',
          last_name: '',
          status: '',
          phone: '',
          email: '',
          address: '',
          occupation: '',
          monthly_income_range: ''
        }
      },
      nextOfKin: {
        first_name: '',
        middle_name: '',
        last_name: '',
        relationship: '',
        phone: '',
        email: '',
        address: '',
        notes: ''
      },
      healthCondition: '',
      healthNotes: '',
      medicalNotes: '',
      medicalAssessedDate: '',
      medicalDocuments: '',
      location: {
        country: '',
        city: ''
      },
      inspiration: '',
      hobbies: '',
      // Juz the child is on when first registered (Hafiz types only, new
      // profiles only); saved as the first hafiz_juz_progress entry
      initialJuz: '',
      sponsorship: {
        statusId: '',
        priorityId: '',
        leftProgrammeAt: ''
      },
      assignedStaffId: '',
      caseNotes: '',
      createdBy: ''
    },
    resolver: yupResolver(beneficiaryScheme)
  });
  const {
    handleSubmit: RHFHandleSubmit,
    watch,
    setValue,
    getValues,
    reset
  } = methods;

  const values = watch();
  const {
    location: { country },
    dateOfBirth
  } = values;

  // Unfinished new-beneficiary forms are kept in this browser (see
  // beneficiaryDraft.js / hooks/useFormDraft)
  const { pendingDraft, draftSavedAt, continueDraft, discardDraft } =
    useFormDraft({
      store: beneficiaryDraftStore,
      scope: user?.id,
      enabled: !isEdit,
      values,
      reset,
      getValues
    });

  useEffect(() => {
    const age = dateOfBirth ? calculateAge(dateOfBirth) : '';
    setValue('age', age);
  }, [dateOfBirth, setValue]);

  // Set default sponsorship status to "Ready For Sponsorship" for new beneficiaries
  useEffect(() => {
    if (!isEdit && sponsorshipStatuses && sponsorshipStatuses.length > 0) {
      const readyStatus = sponsorshipStatuses.find(status =>
        status.status.toLowerCase().includes('ready')
      );
      if (readyStatus && !getValues('sponsorship.statusId')) {
        setValue('sponsorship.statusId', readyStatus.id);
      }
    }
  }, [isEdit, sponsorshipStatuses, setValue, getValues]);

  // Set default health condition to "Healthy / Stable" for new beneficiaries
  useEffect(() => {
    if (!isEdit && sponsorshipConditions && sponsorshipConditions.length > 0) {
      const healthyCondition = sponsorshipConditions.find(
        condition =>
          condition.condition.toLowerCase().includes('healthy') ||
          condition.condition.toLowerCase().includes('stable')
      );
      if (healthyCondition && !getValues('healthCondition')) {
        setValue('healthCondition', healthyCondition.id);
      }
    }
  }, [isEdit, sponsorshipConditions, setValue, getValues]);

  // Set default priority to "Normal" for new beneficiaries
  useEffect(() => {
    if (!isEdit && sponsorshipPriorities && sponsorshipPriorities.length > 0) {
      const normalPriority = sponsorshipPriorities.find(priority =>
        priority.priority_type.toLowerCase().includes('normal')
      );
      if (normalPriority && !getValues('sponsorship.priorityId')) {
        setValue('sponsorship.priorityId', normalPriority.id);
      }
    }
  }, [isEdit, sponsorshipPriorities, setValue, getValues]);

  useEffect(() => {
    if (isEdit && data) {
      const {
        DOB,
        first_name,
        middle_name,
        last_name,
        gender,
        language,
        secondary_language,
        hy_connect_id,
        address,
        country,
        city,
        sponsorship_condition_id,
        health_notes,
        inspiration,
        hobbies,
        sponsorship_priority_id,
        image,
        guardian,
        sponsorship_types,
        assigned_staff_id,
        beneficiary_category,
        enrollment_date,
        created_at,
        medical_assessed_date,
        medical_documents,
        medical_notes,
        case_notes
      } = data;
      setValue('firstName', first_name);
      setValue('middleName', middle_name);
      setValue('lastName', last_name);
      setValue('assignedStaffId', assigned_staff_id || '');
      setValue('beneficiaryCategory', beneficiary_category || '');
      setValue('caseNotes', case_notes || '');
      setValue('medicalNotes', medical_notes || '');
      // Parse medical documents - jsonb returns pre-parsed array; legacy rows may be a JSON string or URL string
      let parsedDocs = [];
      if (Array.isArray(medical_documents)) {
        parsedDocs = medical_documents;
      } else if (typeof medical_documents === 'string' && medical_documents) {
        try {
          parsedDocs = JSON.parse(medical_documents);
          if (!Array.isArray(parsedDocs)) parsedDocs = [medical_documents];
        } catch {
          parsedDocs = [medical_documents];
        }
      }
      setValue('medicalDocuments', parsedDocs);
      // Native <input type="date"> needs a YYYY-MM-DD string, not a Date object.
      if (enrollment_date) {
        setValue(
          'enrollmentDate',
          format(parseISO(enrollment_date), 'yyyy-MM-dd')
        );
      }
      if (created_at) {
        setValue('createdAt', format(parseISO(created_at), 'yyyy-MM-dd'));
      }
      if (medical_assessed_date) {
        setValue(
          'medicalAssessedDate',
          format(parseISO(medical_assessed_date), 'yyyy-MM-dd')
        );
      }
      const locationValues = {
        country,
        city
      };
      setValue('location', locationValues);
      const guardianValues = {
        inheritFrom:
          guardian?.parent_id && data.parents
            ? data.parents.find(p => p.id === guardian.parent_id)
                ?.parent_type || ''
            : '',
        relationship: guardian?.relationship || null,
        firstName: guardian?.first_name || '',
        middleName: guardian?.middle_name || '',
        lastName: guardian?.last_name || '',
        email: guardian?.email || '',
        phone: guardian?.phone || '',
        address: guardian?.address || '',
        occupation: guardian?.occupation || '',
        monthlyIncomeRange: guardian?.monthly_income_range || '',
        notes: guardian?.notes || ''
      };
      setValue('guardian', guardianValues);

      // Hydrate parents (father/mother) and next-of-kin
      const parentsArr = data.parents || [];
      const fatherRow = parentsArr.find(p => p.parent_type === 'father') || {};
      const motherRow = parentsArr.find(p => p.parent_type === 'mother') || {};
      const blankParent = {
        first_name: '',
        middle_name: '',
        last_name: '',
        status: '',
        phone: '',
        email: '',
        address: '',
        occupation: '',
        monthly_income_range: ''
      };
      setValue('parents', {
        father: { ...blankParent, ...fatherRow },
        mother: { ...blankParent, ...motherRow }
      });

      const nokRow = data.next_of_kin || {};
      setValue('nextOfKin', {
        first_name: nokRow.first_name || '',
        middle_name: nokRow.middle_name || '',
        last_name: nokRow.last_name || '',
        relationship: nokRow.relationship || '',
        phone: nokRow.phone || '',
        email: nokRow.email || '',
        address: nokRow.address || '',
        notes: nokRow.notes || ''
      });
      setValue('gender', gender);
      setValue('language', language || '');
      setValue('secondaryLanguage', secondary_language || '');
      setValue('hyConnectId', hy_connect_id || '');
      setValue('address', address);
      setValue('healthNotes', health_notes);
      setValue('inspiration', inspiration);
      setValue('hobbies', hobbies || '');
      setValue('healthCondition', sponsorship_condition_id);
      setValue('image', image);
      if (DOB) {
        setValue('dateOfBirth', format(parseISO(DOB), 'yyyy-MM-dd'));
      }

      // Set sponsorship types from junction table
      if (sponsorship_types && sponsorship_types.length > 0) {
        setValue('sponsorshipTypes', sponsorship_types);
      }

      const sponsorshipValues = {
        statusId: data.sponsorship_status_id || '',
        priorityId: sponsorship_priority_id,
        leftProgrammeAt: data.left_programme_at || ''
      };
      setValue('sponsorship', sponsorshipValues);
    }
  }, [data, isEdit, setValue]);

  // const handleRadioChange = e => {
  //   const { value, name } = e.target;
  //   setValue(name, value);
  // };
  const { upload, uploadDocument, loading: uploadLoading } = useUpload();

  const handleSubmit = async formData => {
    console.log('handleSubmit called', formData);

    // Image is optional. If a File was provided, upload it; if a URL string,
    // keep it; otherwise save without an image. A failed upload must not lose
    // the whole form: the child is saved without the file and the user is
    // told to add it from the profile.
    const failedUploads = [];
    let imageUrl = null;
    if (formData.image) {
      if (typeof formData.image === 'string') {
        imageUrl = formData.image;
      } else {
        try {
          imageUrl = await upload(formData.image);
        } catch (e) {
          // Error already shown by useUpload hook; keep any existing photo
          imageUrl = isEdit ? data?.image || null : null;
          failedUploads.push('the photo');
        }
      }
    }

    // Handle medical documents upload (supports multiple files)
    let medicalDocsUrls = [];
    if (formData.medicalDocuments && Array.isArray(formData.medicalDocuments)) {
      console.log(
        'Uploading medical docs...',
        formData.medicalDocuments.length,
        'files'
      );
      for (const doc of formData.medicalDocuments) {
        if (doc instanceof File) {
          // Upload new file to documents bucket
          try {
            const url = await uploadDocument(doc, 'medical-documents');
            medicalDocsUrls.push(url);
          } catch (e) {
            // Error already shown by useUpload hook
            failedUploads.push(`the document "${doc.name}"`);
          }
        } else if (typeof doc === 'string') {
          // Keep existing URL
          medicalDocsUrls.push(doc);
        }
      }
      console.log('Medical docs uploaded:', medicalDocsUrls);
    } else if (
      formData.medicalDocuments &&
      formData.medicalDocuments instanceof File
    ) {
      // Handle single file for backwards compatibility
      try {
        const url = await uploadDocument(
          formData.medicalDocuments,
          'medical-documents'
        );
        medicalDocsUrls = [url];
      } catch (e) {
        failedUploads.push(`the document "${formData.medicalDocuments.name}"`);
      }
    } else if (
      typeof formData.medicalDocuments === 'string' &&
      formData.medicalDocuments
    ) {
      // Keep existing URL string (legacy)
      medicalDocsUrls = [formData.medicalDocuments];
    }
    // Store as JSON array string for database
    const medicalDocsUrl =
      medicalDocsUrls.length > 0 ? JSON.stringify(medicalDocsUrls) : null;

    console.log('Building submit data...');

    const {
      firstName,
      middleName,
      lastName,
      gender,
      dateOfBirth,
      language,
      secondaryLanguage,
      hyConnectId,
      address,
      sponsorshipTypes,
      guardian,
      healthCondition,
      healthNotes,
      location,
      inspiration,
      hobbies,
      initialJuz,
      sponsorship,
      assignedStaffId,
      beneficiaryCategory,
      enrollmentDate,
      createdAt,
      medicalAssessedDate,
      medicalNotes,
      caseNotes,
      parents,
      nextOfKin
    } = formData;

    // Build parent rows (only include sides with at least one populated field)
    const parentHasContent = p =>
      p && (p.first_name || p.last_name || p.phone || p.address || p.status);
    const parentRows = [];
    if (parentHasContent(parents?.father)) {
      parentRows.push({ ...parents.father, parent_type: 'father' });
    }
    if (parentHasContent(parents?.mother)) {
      parentRows.push({ ...parents.mother, parent_type: 'mother' });
    }

    const formattedDateOfBirth = new Date(dateOfBirth).toISOString();
    // created_at is a timestamp: only send it when the date was changed, so an
    // untouched record keeps its exact time (and a new one gets now() from
    // the DB). Midday keeps the chosen day the same in every timezone.
    const savedCreatedAt =
      isEdit && data?.created_at
        ? format(parseISO(data.created_at), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd');
    const createdAtChanged = createdAt && createdAt !== savedCreatedAt;
    const submitData = {
      DOB: formattedDateOfBirth,
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      gender,
      language,
      secondary_language: secondaryLanguage || null,
      hy_connect_id: hyConnectId || null,
      address,
      country: location.country,
      city: location.city,
      sponsorship_condition_id: healthCondition || null,
      health_notes: healthNotes,
      inspiration: inspiration || null,
      hobbies: hobbies || null,
      ...(!isEdit && initialJuz ? { initial_juz: Number(initialJuz) } : {}),

      // Store sponsorship types as array
      sponsorship_types: sponsorshipTypes,
      // Use first type ID for backwards compatibility
      sponsorship_type_id: sponsorshipTypes?.[0]?.typeId || null,

      duration: 'monthly',
      sponsorship_status_id: sponsorship.statusId || null,
      // Left-programme date only applies to grey statuses; the DB stamps today
      // when a grey status is chosen and no date is given. Leaving the key out
      // (undefined) keeps an existing date; non-grey statuses clear it.
      left_programme_at: sponsorshipStatuses?.find(
        s => s.id === sponsorship.statusId
      )?.is_hidden
        ? sponsorship.leftProgrammeAt || undefined
        : null,
      sponsorship_priority_id: sponsorship.priorityId || null,
      assigned_staff_id: assignedStaffId || null,

      // Profile fields
      beneficiary_category: beneficiaryCategory || null,
      // Postgres `date` columns: format from local components so a date
      // picked in a UTC+ timezone isn't shifted back when cast to date.
      enrollment_date: enrollmentDate
        ? format(new Date(enrollmentDate), 'yyyy-MM-dd')
        : null,
      ...(createdAtChanged
        ? { created_at: new Date(`${createdAt}T12:00:00`).toISOString() }
        : {}),

      // Medical fields
      medical_assessed_date: medicalAssessedDate
        ? format(new Date(medicalAssessedDate), 'yyyy-MM-dd')
        : null,
      medical_documents: medicalDocsUrl,
      medical_notes: medicalNotes || null,

      // Case management
      case_notes: caseNotes || null,

      image: imageUrl,
      guardian: {
        first_name: guardian.firstName,
        middle_name: guardian.middleName,
        last_name: guardian.lastName,
        email: guardian.email,
        phone: guardian.phone,
        address: guardian.address,
        relationship: guardian.relationship || null,
        occupation: guardian.occupation || null,
        monthly_income_range: guardian.monthlyIncomeRange || null,
        notes: guardian.notes || null
      },

      // New optional sections
      parents: parentRows,
      next_of_kin: nextOfKin || null,

      // Editing must not move the child: keep the branch the record already
      // has. Only a brand-new child takes the branch currently selected.
      branch_id: isEdit ? data?.branch_id || selectedBranchId : selectedBranchId,
      organisation_id: organization.id
    };
    if (failedUploads.length) {
      toast.warning(
        `Saving without ${failedUploads.join(', ')} - it did not upload. ` +
          "Add it again from the child's profile.",
        { autoClose: 12000 }
      );
    }
    console.log('Calling onSubmit with:', submitData);
    onSubmit(submitData);
    console.log('onSubmit called');
  };

  const handleFormError = errors => {
    // Get first error message to show in toast
    const getFirstError = obj => {
      for (const [key, value] of Object.entries(obj)) {
        if (value?.message) {
          return value.message;
        } else if (typeof value === 'object' && value !== null) {
          const nested = getFirstError(value);
          if (nested) return nested;
        }
      }
      return null;
    };

    const firstError = getFirstError(errors);
    if (firstError) {
      toast.error(firstError);
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  return (
    <FormProvider
      methods={methods}
      onSubmit={RHFHandleSubmit(handleSubmit, handleFormError)}
    >
      <DraftPrompt
        pendingDraft={pendingDraft}
        onContinue={continueDraft}
        onDiscard={discardDraft}
        note="Photos and documents will need adding again"
      />
      <Row className="pt-3 gx-3">
        <Col md={9}>
          <ProfileInformation
            sectionRef={sectionRefs[1]}
            sideNavLink={sideNavLinks[0]?.href?.substring(1)}
          />

          <SponsorshipTypeCard
            sectionRef={sectionRefs[2]}
            sideNavLink={sideNavLinks[1]?.href?.substring(1)}
            isEdit={isEdit}
          />

          <LocationCard
            sectionRef={sectionRefs[3]}
            sideNavLink={sideNavLinks[2]?.href?.substring(1)}
            country={country}
          />

          <HealthConditionCard
            sectionRef={sectionRefs[4]}
            sideNavLink={sideNavLinks[3]?.href?.substring(1)}
          />

          <ParentsDetails
            sectionRef={sectionRefs[5]}
            sideNavLink={sideNavLinks[4]?.href?.substring(1)}
            locationCountry={country}
          />

          <GuardianDetails
            sectionRef={sectionRefs[6]}
            sideNavLink={sideNavLinks[5]?.href?.substring(1)}
            locationCountry={country}
          />

          <NextOfKinDetails
            sectionRef={sectionRefs[7]}
            sideNavLink={sideNavLinks[6]?.href?.substring(1)}
            locationCountry={country}
          />

          <InspirationCard
            sectionRef={sectionRefs[8]}
            sideNavLink={sideNavLinks[7]?.href?.substring(1)}
          />

          <SponsorshipStatus
            sectionRef={sectionRefs[9]}
            sideNavLink={sideNavLinks[8]?.href?.substring(1)}
          />

          <Card className="border shadow-none mb-3">
            <Card.Body className="d-flex flex-row justify-content-end align-items-center gap-3">
              {!isEdit && <DraftSavedNote savedAt={draftSavedAt} />}
              <LoadingButton
                loading={loading || uploadLoading}
                type="submit"
                className="w-100 w-sm-25"
              >
                {isEdit ? 'Update' : 'Add'}
              </LoadingButton>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="order-0 order-md-1 d-none d-md-block">
          <Card
            className={'mb-3 sticky-top border shadow-none'}
            style={{ top: stickyStatus ? '5rem' : '0' }}
          >
            <Card.Body>
              <div>
                <Nav className="flex-column" variant="pills">
                  {sideNavLinks?.map(item => (
                    <Nav.Link
                      key={item.id}
                      href={item.href}
                      className={`${
                        activeSection === item.id
                          ? 'current custom-nav-link'
                          : 'custom-nav-link'
                      }`}
                      active={activeSection >= item.id}
                    >
                      {item.title}
                    </Nav.Link>
                  ))}
                </Nav>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </FormProvider>
  );
};

BeneficiaryForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  loading: PropTypes.bool,
  data: PropTypes.object,
  costData: PropTypes.array
};

export default BeneficiaryForm;
